import React from 'react';
import './DashboardLinks.css';
import TileTags from './TileTags';
import { SubDashboardLink } from '../../shared/types';
import { trackLinkClick } from '../../services/analyticsService';

interface DashboardLink {
  name: string;
  url: string;
  tags: string[];
  description?: string;
  subDashboards?: SubDashboardLink[];
  isHeading?: boolean;
}

interface DashboardLinksProps {
  dashboards: DashboardLink[];
  onDashboardClick: (dashboard: DashboardLink) => void;
  selectedRole: string | null;
  searchQuery: string;
  tileName: string;
}

const DashboardLinks: React.FC<DashboardLinksProps> = ({ dashboards, onDashboardClick, selectedRole, searchQuery, tileName }) => {
  const handleDashboardClick = (e: React.MouseEvent, dashboard: DashboardLink) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[GA4 Debug] Dashboard link clicked:', dashboard.name);
    trackLinkClick(dashboard.name, dashboard.url, 'dashboard_link', tileName || 'Unknown');
    onDashboardClick(dashboard);
  };

  const handleSubDashboardClick = (e: React.MouseEvent, subDashboard: SubDashboardLink) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[GA4 Debug] Sub-dashboard link clicked:', subDashboard.name);
    trackLinkClick(subDashboard.name, subDashboard.url, 'sub_dashboard', tileName || 'Unknown');
    onDashboardClick({ name: subDashboard.name, url: subDashboard.url, tags: [], description: subDashboard.description });
  };

  // Keep a parent visible when its name or any nested dashboard matches.
  const filteredDashboards = dashboards.reduce<DashboardLink[]>((matches, dashboard) => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const dashboardMatches = normalizedQuery === '' ||
      dashboard.name.toLowerCase().includes(normalizedQuery) ||
      dashboard.tags.some(tag => tag.toLowerCase().includes(normalizedQuery));
    const hasMatchingSubDashboard = dashboard.subDashboards?.some(subDashboard =>
      subDashboard.name.toLowerCase().includes(normalizedQuery) ||
      subDashboard.description?.toLowerCase().includes(normalizedQuery)
    );
    const roleMatch = selectedRole ? dashboard.tags.includes(selectedRole) : true;
    const searchMatch = dashboardMatches || Boolean(hasMatchingSubDashboard);

    if (roleMatch && searchMatch) {
      matches.push({
        ...dashboard,
        subDashboards: dashboard.subDashboards
      });
    }

    return matches;
  }, []);

  return (
    <div className="dashboard-links">
      {filteredDashboards.map((dashboard, index) => {
        const hasSubDashboards = Boolean(dashboard.subDashboards && dashboard.subDashboards.length > 0);
        const isHeading = Boolean(dashboard.isHeading);
        return (
        <div 
          key={index} 
          className={`dashboard-link-item${hasSubDashboards ? ' has-sub-dashboards' : ''}${isHeading ? ' is-heading' : ''}`}
          title={dashboard.description}
          onClick={isHeading ? undefined : (e) => handleDashboardClick(e, dashboard)}
        >
          <div className="dashboard-link-header">
            <div className="dashboard-link-button">
              {dashboard.name}
            </div>
            {!isHeading && <span className="dashboard-link-arrow" aria-hidden="true">&#8594;</span>}
          </div>
          {hasSubDashboards && (
            <div className="sub-dashboard-list">
              {dashboard.subDashboards!.map((subDashboard, subIndex) => (
                <div
                  key={subIndex}
                  className="sub-dashboard-item"
                  title={subDashboard.description}
                  onClick={(e) => handleSubDashboardClick(e, subDashboard)}
                >
                  <span className="sub-dashboard-name">{subDashboard.name}</span>
                  <span className="sub-dashboard-arrow" aria-hidden="true">&#8594;</span>
                </div>
              ))}
            </div>
          )}
          <TileTags tags={dashboard.tags} />
        </div>
        );
      })}
    </div>
  );
};

export default DashboardLinks;